import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  LinearScale,
  Plugin,
  ScriptableContext,
  Tooltip,
} from 'chart.js';
import {
  sitePerformanceChartJsBarValueLabelPlugin,
  sitePerformanceChartJsYAxisWidthPlugin,
} from '../../plugins/sitePerformanceChartJsBarValueLabelPlugin';
import { sitePerformanceChartJsBarSpacingPlugin } from '../../plugins/sitePerformanceChartJsBarSpacingPlugin';
import { sitePerformanceChartJsCategoryGridPlugin } from '../../plugins/sitePerformanceChartJsCategoryGridPlugin';
import {
  SITE_PERFORMANCE_CHART_BAR_HEIGHT,
  SITE_PERFORMANCE_CHART_COLUMN_LABEL_ONE,
  SITE_PERFORMANCE_CHART_COLUMN_LABEL_TWO,
  SITE_PERFORMANCE_CHART_JS_LAYOUT_PADDING_TOP,
  SITE_PERFORMANCE_CHART_Y_AXIS_LABEL_PADDING,
  SITE_PERFORMANCE_CHART_Y_AXIS_LEFT_TICK_LENGTH,
} from 'app/modules/site-management/widgets/fleet-dashboard/constants/SitePerformanceChart.constants';
import {
  SitePerformanceChartDatum,
  SitePerformanceChartUtils,
} from 'app/modules/site-management/widgets/fleet-dashboard/utils/SitePerformanceChartUtils';
import { ReactChartUtils } from 'app/utils/react-charts/ReactChartUtils';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';

interface CreateSitePerformanceChartJsDataParams {
  pageActualData: SitePerformanceChartDatum[];
  pagePlannedData: SitePerformanceChartDatum[];
  isGcd: boolean;
  hoveredSiteIndex: number | null;
}

export const sitePerformanceChartJsPlugins: Plugin<'bar'>[] = [
  sitePerformanceChartJsYAxisWidthPlugin,
  sitePerformanceChartJsBarSpacingPlugin,
  sitePerformanceChartJsCategoryGridPlugin,
  sitePerformanceChartJsBarValueLabelPlugin,
];

let areBuiltInElementsRegistered = false;

export const registerSitePerformanceChartJsBuiltInElements = (): void => {
  if (areBuiltInElementsRegistered) {
    return;
  }

  ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

  areBuiltInElementsRegistered = true;
};

export const createSitePerformanceChartJsData = ({
  pageActualData,
  pagePlannedData,
  isGcd,
  hoveredSiteIndex,
}: CreateSitePerformanceChartJsDataParams): ChartData<'bar'> => {
  const labels = pageActualData.map(datum => ReactChartUtils.extractColumnName(datum.primary));
  const datasets: ChartData<'bar'>['datasets'] = [
    {
      label: SITE_PERFORMANCE_CHART_COLUMN_LABEL_ONE,
      data: pageActualData.map(datum => datum.secondary),
      backgroundColor: (context: ScriptableContext<'bar'>): string => {
        if (hoveredSiteIndex === null) {
          return kccThemeConstants.COLORS.PETROL[500];
        }

        return context.dataIndex === hoveredSiteIndex
          ? kccThemeConstants.COLORS.PETROL[500]
          : kccThemeConstants.COLORS.PETROL[100];
      },
      borderRadius: 0,
      borderSkipped: false,
      barThickness: SITE_PERFORMANCE_CHART_BAR_HEIGHT,
    },
  ];

  if (isGcd) {
    datasets.push({
      label: SITE_PERFORMANCE_CHART_COLUMN_LABEL_TWO,
      data: pagePlannedData.map(datum => datum.secondary),
      backgroundColor: (context: ScriptableContext<'bar'>): string => {
        if (hoveredSiteIndex === null) {
          return kccThemeConstants.COLORS.GRAY_250;
        }

        return context.dataIndex === hoveredSiteIndex
          ? kccThemeConstants.COLORS.GRAY_250
          : kccThemeConstants.COLORS.GRAY_100;
      },
      borderRadius: 0,
      borderSkipped: false,
      barThickness: SITE_PERFORMANCE_CHART_BAR_HEIGHT,
    });
  }

  return { labels, datasets };
};

export const createSitePerformanceChartJsOptions = (labels: ChartData<'bar'>['labels']): ChartOptions<'bar'> => ({
  indexAxis: 'y' as const,
  responsive: true,
  maintainAspectRatio: false,
  animations: {
    y: {
      duration: 0,
    },
  },
  interaction: {
    mode: 'index',
    axis: 'y',
    intersect: false,
  },
  layout: {
    padding: {
      top: SITE_PERFORMANCE_CHART_JS_LAYOUT_PADDING_TOP,
      right: 8,
      bottom: 0,
      left: 0,
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: false,
    },
  },
  scales: {
    x: {
      display: false,
      min: 0,
      grid: {
        display: false,
      },
      border: {
        display: false,
      },
    },
    y: {
      grid: {
        display: false,
      },
      border: {
        display: false,
      },
      ticks: {
        display: true,
        padding: SITE_PERFORMANCE_CHART_Y_AXIS_LEFT_TICK_LENGTH + SITE_PERFORMANCE_CHART_Y_AXIS_LABEL_PADDING,
        color: kccThemeConstants.COLORS.TEXT_SECONDARY,
        font: {
          family: kccThemeConstants.TYPOGRAPHY.FONT_FAMILY,
          size: kccThemeConstants.TYPOGRAPHY.FONT_SIZE.SM,
          weight: kccThemeConstants.TYPOGRAPHY.FONT_WEIGHT.REGULAR,
          lineHeight: kccThemeConstants.TYPOGRAPHY.LINE_HEIGHT.BASE,
        },
        callback: (_value, index): string | string[] => {
          const label = labels?.[index];

          return label ? SitePerformanceChartUtils.wrapYAxisLabel(String(label)) : '';
        },
      },
    },
  },
});
